/**
 * Audio Engine - 1994-era PC Sound Generator
 *
 * Generates 4-channel PC speaker / AdLib style sounds using Web Audio API.
 * All sounds are synthesized programmatically - no audio files needed.
 *
 * Characteristics:
 * - Square/sawtooth/triangle waves
 * - Short attack, quick decay
 * - Frequency sweeps for "dial" feel
 * - Simple notification beeps
 */

import type { SoundId } from '../shared/types.js';
import { AUDIO, ENVELOPE, DTMF, PHONE_RING } from './audio-config.js';

// =============================================================================
// Sound Definitions
// =============================================================================

type SoundType = 'tone' | 'sweep' | 'double' | 'click' | 'chirp';

interface SoundDef {
  type: SoundType;
  oscillator: OscillatorType;
  // For 'tone' and 'click'
  freq?: number;
  // For 'sweep'
  startFreq?: number;
  endFreq?: number;
  // For 'double' (two-tone)
  freqs?: [number, number];
  // Duration in ms
  duration: number;
  // Volume (0-1)
  volume?: number;
}

/**
 * Sound definitions - all 4-channel PC speaker style
 */
const SOUNDS: Record<string, SoundDef> = {
  // Phone/Dial sounds
  'phone-ring': {
    type: 'double',
    oscillator: 'sine',
    freqs: [440, 480],
    duration: 1000,
    volume: 0.2,
  },
  'dial-tone': {
    type: 'tone',
    oscillator: 'sine',
    freq: 350,
    duration: 500,
    volume: 0.15,
  },
  'dial-tone-start': {
    // Continuous dial tone - 350Hz + 440Hz combined (US standard)
    type: 'double',
    oscillator: 'sine',
    freqs: [350, 440],
    duration: 2000,
    volume: 0.12,
  },
  'ringback-tone': {
    // The "ring ring" the caller hears (different from actual phone ring)
    // US standard: 440Hz + 480Hz, 2 seconds on, 4 seconds off
    type: 'double',
    oscillator: 'sine',
    freqs: [440, 480],
    duration: 2000,
    volume: 0.1,
  },
  'busy-tone': {
    type: 'tone',
    oscillator: 'sine',
    freq: 480,
    duration: 300,
    volume: 0.15,
  },
  'phone-pickup': {
    // Satisfying click when someone answers - relay/switch sound
    type: 'click',
    oscillator: 'square',
    freq: 800,
    duration: 30,
    volume: 0.25,
  },
  'phone-hangup': {
    type: 'click',
    oscillator: 'square',
    freq: 200,
    duration: 50,
    volume: 0.2,
  },
  'phone-offhook': {
    // Sound when lifting handset
    type: 'click',
    oscillator: 'square',
    freq: 600,
    duration: 25,
    volume: 0.18,
  },

  // FM Tuner sounds
  'radio-tune': {
    type: 'sweep',
    oscillator: 'square',
    startFreq: 800,
    endFreq: 1200,
    duration: 80,
    volume: 0.15,
  },
  'radio-tune-down': {
    type: 'sweep',
    oscillator: 'square',
    startFreq: 1200,
    endFreq: 800,
    duration: 80,
    volume: 0.15,
  },
  'radio-lock': {
    type: 'double',
    oscillator: 'square',
    freqs: [660, 880],
    duration: 100,
    volume: 0.25,
  },
  'radio-squelch': {
    type: 'click',
    oscillator: 'square',
    freq: 2000,
    duration: 20,
    volume: 0.25,
  },
  'radio-tx-start': {
    type: 'tone',
    oscillator: 'sawtooth',
    freq: 440,
    duration: 200,
    volume: 0.15,
  },
  'radio-tx-end': {
    type: 'sweep',
    oscillator: 'triangle',
    startFreq: 600,
    endFreq: 200,
    duration: 300,
    volume: 0.12,
  },
  'ui-beep': {
    type: 'tone',
    oscillator: 'square',
    freq: 1000,
    duration: 50,
    volume: 0.15,
  },

  // System sounds (existing SoundIds mapped to PC speaker style)
  'keyclick': {
    type: 'click',
    oscillator: 'square',
    freq: 1500,
    duration: 10,
    volume: 0.08,
  },
  'keystroke': {
    type: 'click',
    oscillator: 'square',
    freq: 1200,
    duration: 15,
    volume: 0.1,
  },
  'enter': {
    type: 'tone',
    oscillator: 'square',
    freq: 800,
    duration: 30,
    volume: 0.12,
  },
  'error': {
    type: 'double',
    oscillator: 'square',
    freqs: [200, 150],
    duration: 200,
    volume: 0.25,
  },
  'bell': {
    type: 'tone',
    oscillator: 'sine',
    freq: 1000,
    duration: 100,
    volume: 0.2,
  },
  'boot': {
    type: 'chirp',
    oscillator: 'square',
    startFreq: 400,
    endFreq: 1200,
    duration: 500,
    volume: 0.2,
  },
  'shutdown': {
    type: 'sweep',
    oscillator: 'square',
    startFreq: 1000,
    endFreq: 100,
    duration: 800,
    volume: 0.2,
  },
  'hdd-read': {
    type: 'click',
    oscillator: 'square',
    freq: 3000,
    duration: 5,
    volume: 0.05,
  },
  'hdd-write': {
    type: 'click',
    oscillator: 'square',
    freq: 2500,
    duration: 8,
    volume: 0.06,
  },
  'static': {
    type: 'tone',
    oscillator: 'sawtooth',
    freq: 100,
    duration: 50,
    volume: 0.1,
  },
  'radio-static': {
    type: 'tone',
    oscillator: 'sawtooth',
    freq: 80,
    duration: 100,
    volume: 0.08,
  },
};

// =============================================================================
// Audio Engine
// =============================================================================

/**
 * Web Audio based sound generator for 1994-era PC sounds
 */
export class AudioEngine {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private initialized: boolean = false;
  private masterVolume: number = AUDIO.DEFAULT_MASTER_VOLUME;

  /**
   * Get the AudioContext (for sharing with other audio systems like OPL2)
   * Returns null if not initialized
   */
  getContext(): AudioContext | null {
    return this.context;
  }

  /**
   * Get the master gain node (for routing other audio systems through shared volume)
   * Returns null if not initialized
   */
  getMasterGainNode(): GainNode | null {
    return this.masterGain;
  }

  /**
   * Initialize the audio context (must be called after user interaction)
   */
  init(): boolean {
    if (this.initialized) {
      return true;
    }

    try {
      // Create audio context
      this.context = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      // Create master gain node
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = this.masterVolume;
      this.masterGain.connect(this.context.destination);

      this.initialized = true;
      return true;
    } catch (e) {
      console.warn('Web Audio API not available:', e);
      return false;
    }
  }

  /**
   * Resume audio context if suspended (needed for some browsers)
   */
  private async ensureResumed(): Promise<boolean> {
    if (!this.context) {
      return false;
    }

    if (this.context.state === 'suspended') {
      try {
        await this.context.resume();
      } catch (e) {
        console.warn('Failed to resume audio context:', e);
        return false;
      }
    }

    return true;
  }

  /**
   * Set master volume (0-1)
   */
  setVolume(volume: number): void {
    // Handle undefined/NaN by keeping current volume
    if (typeof volume !== 'number' || isNaN(volume)) {
      return;
    }
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  /**
   * Get current master volume
   */
  getVolume(): number {
    return this.masterVolume;
  }

  /**
   * Play a sound by ID
   */
  async play(soundId: string): Promise<void> {
    if (!this.initialized && !this.init()) {
      return;
    }

    if (!await this.ensureResumed()) {
      return;
    }

    // Handle DTMF tones (dtmf:0, dtmf:1, etc.)
    if (soundId.startsWith('dtmf:')) {
      const digit = soundId.slice(5);
      this.playDTMF(digit, 100);
      return;
    }

    const def = SOUNDS[soundId];
    if (!def) {
      // Unknown sound - play a default beep
      this.playTone(AUDIO.DEFAULT_BEEP_FREQUENCY, AUDIO.DEFAULT_BEEP_DURATION_MS, 'square', AUDIO.DEFAULT_BEEP_VOLUME);
      return;
    }

    switch (def.type) {
      case 'tone':
        this.playTone(def.freq!, def.duration, def.oscillator, def.volume);
        break;
      case 'sweep':
        this.playFrequencySweep(def.startFreq!, def.endFreq!, def.duration, def.oscillator, def.volume);
        break;
      case 'double':
        this.playDoubleTone(def.freqs![0], def.freqs![1], def.duration, def.oscillator, def.volume);
        break;
      case 'click':
        this.playClick(def.freq!, def.duration, def.oscillator, def.volume);
        break;
      case 'chirp':
        this.playChirp(def.startFreq!, def.endFreq!, def.duration, def.oscillator, def.volume);
        break;
    }
  }

  /**
   * Play a simple tone
   */
  playTone(
    frequency: number,
    durationMs: number,
    type: OscillatorType = 'square',
    volume: number = 0.15
  ): void {
    if (!this.context || !this.masterGain) return;

    const oscillator = this.context.createOscillator();
    const gainNode = this.context.createGain();

    oscillator.type = type;
    oscillator.frequency.value = frequency;

    // Quick attack, quick decay envelope
    const now = this.context.currentTime;
    const duration = durationMs / 1000;

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + ENVELOPE.TONE_ATTACK_S); // 5ms attack
    gainNode.gain.setValueAtTime(volume, now + duration - ENVELOPE.TONE_DECAY_S);
    gainNode.gain.linearRampToValueAtTime(0, now + duration); // 10ms decay

    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    oscillator.start(now);
    oscillator.stop(now + duration);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play a frequency sweep (for tuning dial feel)
   */
  playFrequencySweep(
    startFreq: number,
    endFreq: number,
    durationMs: number,
    type: OscillatorType = 'square',
    volume: number = 0.15
  ): void {
    if (!this.context || !this.masterGain) return;

    const oscillator = this.context.createOscillator();
    const gainNode = this.context.createGain();

    oscillator.type = type;

    const now = this.context.currentTime;
    const duration = durationMs / 1000;

    // Frequency sweep
    oscillator.frequency.setValueAtTime(startFreq, now);
    oscillator.frequency.linearRampToValueAtTime(endFreq, now + duration);

    // Envelope
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + ENVELOPE.SWEEP_ATTACK_S);
    gainNode.gain.setValueAtTime(volume, now + duration - ENVELOPE.SWEEP_DECAY_S);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);

    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    oscillator.start(now);
    oscillator.stop(now + duration);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play a two-tone beep (success/found sound)
   */
  playDoubleTone(
    freq1: number,
    freq2: number,
    durationMs: number,
    type: OscillatorType = 'square',
    volume: number = 0.15
  ): void {
    if (!this.context || !this.masterGain) return;

    const halfDuration = durationMs / 2;

    // First tone
    this.playTone(freq1, halfDuration, type, volume);

    // Second tone after first
    setTimeout(() => {
      this.playTone(freq2, halfDuration, type, volume);
    }, halfDuration);
  }

  /**
   * Play a sharp click/pop sound
   */
  playClick(
    frequency: number,
    durationMs: number,
    type: OscillatorType = 'square',
    volume: number = 0.2
  ): void {
    if (!this.context || !this.masterGain) return;

    const oscillator = this.context.createOscillator();
    const gainNode = this.context.createGain();

    oscillator.type = type;
    oscillator.frequency.value = frequency;

    const now = this.context.currentTime;
    const duration = durationMs / 1000;

    // Very sharp envelope for click
    gainNode.gain.setValueAtTime(volume, now);
    gainNode.gain.exponentialRampToValueAtTime(ENVELOPE.CLICK_EXPONENTIAL_MIN, now + duration);

    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    oscillator.start(now);
    oscillator.stop(now + duration);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play a chirp (boot sound style)
   */
  playChirp(
    startFreq: number,
    endFreq: number,
    durationMs: number,
    type: OscillatorType = 'square',
    volume: number = 0.2
  ): void {
    if (!this.context || !this.masterGain) return;

    const oscillator = this.context.createOscillator();
    const gainNode = this.context.createGain();

    oscillator.type = type;

    const now = this.context.currentTime;
    const duration = durationMs / 1000;

    // Exponential frequency sweep for chirp effect
    oscillator.frequency.setValueAtTime(startFreq, now);
    oscillator.frequency.exponentialRampToValueAtTime(endFreq, now + duration * ENVELOPE.CHIRP_FREQ_DURATION_FACTOR);
    oscillator.frequency.setValueAtTime(endFreq, now + duration);

    // Envelope with sustain
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + ENVELOPE.CHIRP_ATTACK_S);
    gainNode.gain.setValueAtTime(volume, now + duration * ENVELOPE.CHIRP_SUSTAIN_FACTOR);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);

    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    oscillator.start(now);
    oscillator.stop(now + duration);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play DTMF tones (two simultaneous frequencies)
   * Used by the Dial telephone program
   */
  playDTMF(button: string, durationMs: number = DTMF.DEFAULT_DURATION_MS): void {
    if (!this.context || !this.masterGain) return;

    const DTMF_FREQS: Record<string, [number, number]> = {
      '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
      '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
      '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
      '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
    };

    const freqs = DTMF_FREQS[button];
    if (!freqs) return;

    const now = this.context.currentTime;
    const duration = durationMs / 1000;
    const volume = DTMF.VOLUME;

    // Create two oscillators for the dual-tone
    for (const freq of freqs) {
      const oscillator = this.context.createOscillator();
      const gainNode = this.context.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = freq;

      // Quick attack, sustain, quick decay
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + DTMF.ATTACK_S);
      gainNode.gain.setValueAtTime(volume, now + duration - DTMF.DECAY_S);
      gainNode.gain.linearRampToValueAtTime(0, now + duration);

      oscillator.connect(gainNode);
      gainNode.connect(this.masterGain);

      oscillator.start(now);
      oscillator.stop(now + duration);

      oscillator.onended = () => {
        oscillator.disconnect();
        gainNode.disconnect();
      };
    }
  }

  /**
   * Play a phone ring pattern (ring-pause-ring)
   */
  playPhoneRing(): void {
    if (!this.context || !this.masterGain) return;

    const now = this.context.currentTime;
    const ringDuration = PHONE_RING.DURATION_S;
    const volume = PHONE_RING.VOLUME;

    // Ring is two tones (440Hz and 480Hz) played together
    const ringFreqs = [...PHONE_RING.FREQUENCIES];

    for (const freq of ringFreqs) {
      const oscillator = this.context.createOscillator();
      const gainNode = this.context.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = freq;

      // Ring envelope with cadence
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + PHONE_RING.ATTACK_S);
      gainNode.gain.setValueAtTime(volume, now + ringDuration - PHONE_RING.DECAY_S);
      gainNode.gain.linearRampToValueAtTime(0, now + ringDuration);

      oscillator.connect(gainNode);
      gainNode.connect(this.masterGain);

      oscillator.start(now);
      oscillator.stop(now + ringDuration);

      oscillator.onended = () => {
        oscillator.disconnect();
        gainNode.disconnect();
      };
    }
  }

  /**
   * Check if a sound ID is defined
   */
  hasSound(soundId: string): boolean {
    return soundId in SOUNDS;
  }

  /**
   * Check if audio is initialized and ready
   */
  isReady(): boolean {
    return this.initialized && this.context !== null;
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

/** Global audio engine instance */
export const audioEngine = new AudioEngine();

// =============================================================================
// Streaming Audio Player (for DOSBox audio)
// =============================================================================

/**
 * Streaming audio player for real-time audio from DOSBox
 * Uses Web Audio API to buffer and play continuous audio samples
 */
export class StreamingAudioPlayer {
  private ctx: AudioContext | null = null;
  private masterOutput: AudioNode | null = null;
  private nextPlayTime: number = 0;
  private enabled: boolean = true;
  private initialized: boolean = false;

  /**
   * Initialize with an existing AudioContext (to share with main audio engine)
   * @param context - AudioContext to use
   * @param masterOutput - Optional node to route output through (for shared volume control)
   */
  init(context?: AudioContext, masterOutput?: AudioNode): boolean {
    if (this.initialized) return true;

    try {
      this.ctx = context || new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      this.masterOutput = masterOutput || null;
      this.initialized = true;
      return true;
    } catch (e) {
      console.warn('StreamingAudioPlayer: Failed to initialize', e);
      return false;
    }
  }

  /**
   * Push audio samples to be played
   * @param samples - Float32Array of audio samples
   * @param rate - Sample rate (e.g., 22050, 44100)
   */
  pushSamples(samples: Float32Array, rate: number): void {
    if (!this.ctx || !this.enabled || samples.length === 0) return;

    // Resume context if suspended (autoplay policy)
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }

    // Create audio buffer and copy samples directly
    // Type assertion needed because copyToChannel expects Float32Array<ArrayBuffer>
    // but samples might be Float32Array<ArrayBufferLike>
    const buffer = this.ctx.createBuffer(1, samples.length, rate);
    buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);

    // Create buffer source
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterOutput || this.ctx.destination);

    // Schedule playback
    const now = this.ctx.currentTime;
    if (this.nextPlayTime < now) {
      this.nextPlayTime = now;
    }
    source.start(this.nextPlayTime);
    this.nextPlayTime += buffer.duration;
  }

  /**
   * Enable or disable audio playback
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Reset the playback time (for when resuming after pause)
   */
  reset(): void {
    this.nextPlayTime = 0;
  }
}
